import { Content } from "@jobber/components/Content";
import { Text } from "@jobber/components/Text";
import { Link } from "@jobber/components/Link";
import { Heading } from "@jobber/components/Heading";
import { Emphasis } from "@jobber/components/Emphasis";
import { StatusLabel } from "@jobber/components/StatusLabel";
import { Card } from "@jobber/components/Card";
import { Fragment } from "react";
import { ConnectionState } from "../../types";
import { IntegrationStatus } from "../../services";
import xeroLogo from "assets/images/xero_logo.svg";
import jobberLogo from "assets/images/jobber_logo.svg";

type ConnectionStatusProps = {
    status: IntegrationStatus | null;
    sessionInvalid: boolean;
    marketplaceUrl: string;
};

function statusVisual(state?: ConnectionState) {
    switch (state) {
        case ConnectionState.Connected: return { label: "Connected", status: "success" } as const;
        case ConnectionState.Throttled: return { label: "Throttled", status: "warning" } as const;
        case ConnectionState.NotConnected: return { label: "Not connected", status: "critical" } as const;
        default: return { label: "Unknown", status: "inactive" } as const;
    }
}

const ConnectionStatus = ({ status, sessionInvalid, marketplaceUrl }: ConnectionStatusProps) => {
    const throttled = status?.xero === ConnectionState.Throttled || status?.jobber === ConnectionState.Throttled;
    const notConnected = sessionInvalid || status?.xero === ConnectionState.NotConnected || status?.jobber === ConnectionState.NotConnected;
    const accent = notConnected ? "red" : throttled ? "yellow" : "green";

    const platforms = [
        { name: "Jobber", logo: jobberLogo, state: status?.jobber, invert: true },
        { name: "Xero", logo: xeroLogo, state: status?.xero, invert: false },
    ];

    return (
        <Card accent={accent}>
            <Content type={"div"} spacing={"base"}>
                <Heading level={2}>Connection status</Heading>
                {sessionInvalid ? (
                    <Text size={"large"}>
                        Error communicating with Jobber. Please <Link url={marketplaceUrl} external={true}>return to the marketplace</Link> and click Manage App to refresh.
                    </Text>
                ) : (
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "auto auto",
                            justifyContent: "start",
                            alignItems: "center",
                            columnGap: "24px",
                            rowGap: "12px",
                        }}
                    >
                        {platforms.map(({ name, logo, state, invert }) => {
                            const visual = statusVisual(state);
                            return (
                                <Fragment key={name}>
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <img
                                            src={logo}
                                            style={{ width: "24px", height: "24px", objectFit: "contain", filter: invert ? "invert(1)" : undefined }}
                                        />
                                        <Text><Emphasis variation={"bold"}>{name}</Emphasis></Text>
                                    </div>
                                    <StatusLabel label={visual.label} status={visual.status} />
                                </Fragment>
                            );
                        })}
                    </div>
                )}
            </Content>
        </Card>
    );
};

export default ConnectionStatus;
